import React, {useEffect, useRef, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../api';

export default function CourseViewer() {
  const {courseId} = useParams();
  const [course, setCourse] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const videoRef = useRef();

  const load = async () => {
    const res = await api(`/lms/my-courses/${courseId}`);
    if (res.ok) {
      const data = await res.json();
      setCourse(data);
      const firstIncomplete = data.pages?.findIndex(p => !p.completed);
      if (firstIncomplete > 0) setCurrentIndex(firstIncomplete);
    }
  };

  useEffect(() => { load(); }, [courseId]);

  const page = course?.pages?.[currentIndex];

  const markComplete = async () => {
    if (!page || page.completed) return;
    await api(`/lms/my-courses/${courseId}/pages/${page.id}/complete`, { method: 'POST' });
    setCourse(prev => {
      const updated = {...prev, pages: prev.pages.map((p, i) => i === currentIndex ? {...p, completed: true} : p)};
      if (updated.pages.every(p => p.completed)) updated.completedAt = new Date().toISOString();
      return updated;
    });
  };

  const goTo = (index) => {
    if (index >= 0 && index < course.pages.length) {
      setCurrentIndex(index);
      setQuizResult(null);
      setQuizAnswers({});
    }
  };

  if (!course) return <Layout><div className="text-center py-12 text-gray-500">Loading...</div></Layout>;

  const completedCount = course.pages?.filter(p => p.completed).length || 0;
  const totalCount = course.pages?.length || 0;
  const pct = totalCount ? Math.round(completedCount / totalCount * 100) : 0;

  return (
    <Layout>
      <div className="mb-4">
        <Link to="/courses" className="text-indigo-600 hover:text-indigo-800 text-sm">← Back to My Courses</Link>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-72 flex-shrink-0">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-900 mb-1">{course.title}</h3>
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full ${course.completedAt ? 'bg-green-500' : 'bg-indigo-600'}`} style={{width: `${pct}%`}} />
              </div>
              <span className="text-xs text-gray-500">{pct}%</span>
            </div>
            <div className="space-y-1">
              {course.pages?.map((p, i) => (
                <button key={p.id} onClick={() => goTo(i)}
                  className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${
                    i === currentIndex ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}>
                  {p.completed
                    ? <span className="text-green-500 flex-shrink-0">✓</span>
                    : <span className="text-gray-300 flex-shrink-0">○</span>}
                  <span className="truncate">{p.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          {page ? (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">{page.title}</h2>
                  <span className="text-sm text-gray-500">Page {currentIndex + 1} of {totalCount}</span>
                </div>

                {page.pageType === 'VIDEO' && page.videoUrl && (
                  <div className="mb-4">
                    <video ref={videoRef} controls onEnded={markComplete}
                      className="w-full rounded-lg bg-black" style={{maxHeight: '500px'}}>
                      <source src={page.videoUrl} />
                    </video>
                  </div>
                )}

                {page.pageType === 'VIDEO' && !page.videoUrl && (
                  <div className="mb-4 bg-gray-100 rounded-lg p-12 text-center text-gray-500">
                    No video available for this page.
                  </div>
                )}

                {page.content && page.pageType !== 'QUIZ' && (
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{__html: page.content}} />
                )}

                {page.pageType === 'QUIZ' && page.content && (() => {
                  let questions = [];
                  try { questions = JSON.parse(page.content); } catch(e) {}
                  return (
                    <div className="space-y-4">
                      {questions.map((q, qi) => (
                        <div key={qi} className="border rounded-lg p-4">
                          <p className="font-medium text-gray-900 mb-2">{qi + 1}. {q.question}</p>
                          <div className="space-y-1">
                            {q.options.map((opt, oi) => (
                              <label key={oi} className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-gray-50 ${
                                quizResult ? (oi === q.correctIndex ? 'bg-green-50' : quizAnswers[qi] === oi ? 'bg-red-50' : '') : ''
                              }`}>
                                <input type="radio" name={`q-${qi}`} disabled={!!quizResult}
                                  checked={quizAnswers[qi] === oi}
                                  onChange={() => setQuizAnswers({...quizAnswers, [qi]: oi})} />
                                <span className="text-sm">{opt}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                      {quizResult && (
                        <div className={`rounded-lg p-4 text-center font-medium ${quizResult.passed ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          Score: {quizResult.score}% ({quizResult.correctCount}/{quizResult.totalCount})
                          {quizResult.passed ? ' — Passed!' : ` — ${course.passingScore}% required to pass`}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="border-t px-6 py-4 flex items-center justify-between">
                <button onClick={() => goTo(currentIndex - 1)} disabled={currentIndex === 0}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 text-sm">
                  ← Previous
                </button>
                <div className="flex items-center gap-3">
                  {page.completed ? (
                    <span className="text-green-600 text-sm font-medium">✓ Completed{page.quizScore != null ? ` (${page.quizScore}%)` : ''}</span>
                  ) : page.pageType === 'QUIZ' ? (
                    <button onClick={async () => {
                      let questions = [];
                      try { questions = JSON.parse(page.content); } catch(e) {}
                      const answers = questions.map((_, i) => quizAnswers[i] ?? -1);
                      const res = await api(`/lms/my-courses/${courseId}/pages/${page.id}/submit-quiz`, {
                        method: 'POST', body: JSON.stringify({ answers }),
                      });
                      if (res.ok) {
                        const result = await res.json();
                        setQuizResult(result);
                        if (result.passed) {
                          setCourse(prev => {
                            const updated = {...prev, pages: prev.pages.map((p, i) => i === currentIndex ? {...p, completed: true, quizScore: result.score} : p)};
                            if (updated.pages.every(p => p.completed)) updated.completedAt = new Date().toISOString();
                            return updated;
                          });
                        }
                      }
                    }} disabled={!!quizResult}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm disabled:opacity-50">
                      Submit Quiz
                    </button>
                  ) : page.pageType === 'CONTENT' ? (
                    <button onClick={markComplete}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                      Mark Complete
                    </button>
                  ) : (
                    <span className="text-gray-500 text-sm">Watch the video to complete</span>
                  )}
                </div>
                <button onClick={() => goTo(currentIndex + 1)} disabled={currentIndex === totalCount - 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 text-sm">
                  Next →
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow text-center py-12 text-gray-500">This course has no pages.</div>
          )}

          {course.completedAt && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4 text-center text-green-700 font-medium">
              🎉 Course completed!
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
